import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import PageContainer from '../layout/PageContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { GET_VENDORS_QUERY } from '../../queries/VendorQueries';
import type { Vendor } from '../../models/Vendor';

const INITIAL_PAGE_SIZE = 100;

export default function VendorList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [getVendors, { loading: getVendorsLoading, error: getVendorsError, data }] = useLazyQuery(
    GET_VENDORS_QUERY);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: Vendor[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      getVendors();

      if (getVendorsLoading) {
        setIsLoading(true);
      }

      if (getVendorsError) {
        setError(getVendorsError);
      }

      setRowsState({
        rows: data?.vendors || [],
        rowCount: data?.vendors.length || 0,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [getVendors, getVendorsLoading, getVendorsError, data?.vendors]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/vendors/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = useCallback(() => {
    navigate('/dashboard/vendors/new');
  }, [navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        width: 200
      },
      {
        field: 'address',
        headerName: 'Address',
        width: 200,
        valueGetter: (_, row) => row.address
      },
      {
        field: 'city',
        headerName: 'City',
        width: 200,
        valueGetter: (_, row) => row.city
      },
      {
        field: 'state',
        headerName: 'State',
        width: 100,
        valueGetter: (_, row) => row.state
      },
      {
        field: 'zip',
        headerName: 'Zip',
        width: 200,
        valueGetter: (_, row) => row.zip
      },
      {
        field: 'contact',
        headerName: 'Contact',
        width: 200,
        valueGetter: (_, row) => row.contact?.name || 'N/A'
      }
    ],
    [],
  );

  const pageTitle = 'Vendors';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <Box sx={{ height: '64vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={rowsState.rows}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              loading={isLoading || getVendorsLoading}
              initialState={initialState}
              showToolbar
              pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: 'transparent',
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: 'none',
                },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: 'pointer',
                },
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}